import { Button, IconButton, Icons } from '@commenda/kakashi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CommendaAiQuestionAnswer } from './CommendaAiActionsContext';
import { QuestionBlockHelper, type QuestionAsked } from './question-block';
import { QUESTION_CARD_SECONDARY_BUTTON } from './question-card-buttons';
import { QuestionCardShell } from './QuestionCardShell';
import { QuestionOptionRow, QuestionWriteInRow } from './QuestionOptionRow';
import { useQuestionCardHotkeys } from './useQuestionCardHotkeys';

const ADVANCE_DELAY_MS = 250;

interface QuestionSelectCardProps {
  isMultiSelect: boolean;
  questions: QuestionAsked[];
  /** True while a turn is generating: rows and actions disable the way the composer's send does. */
  isDisabled: boolean;
  /** Select all and Clear on a pick-many card, with "None of these" rows standing alone. Off, the card
   * renders and toggles as it did before them. */
  isSelectAllEnabled: boolean;
  onAnswer: (answers: CommendaAiQuestionAnswer[]) => void;
  onClose: () => void;
}

/** Options are tracked by position, not title, so two options with the same title stay distinct. */
function titlesFor(question: QuestionAsked, optionIndexes: number[]): string[] {
  return optionIndexes.flatMap((optionIndex) => {
    const option = question.options[optionIndex];
    return option ? [option.title] : [];
  });
}

/** The pick-one and pick-many cards (Figma "Question / Card", Single select and Multi select).
 * State machine ported from apps/admin ResearchClarificationCard: index-keyed answers, a pager for
 * multi-question blocks, 250ms auto-advance, and answers accumulating into one submission. Pick-one
 * taps answer immediately; pick-many collects and sends on Continue. */
export function QuestionSelectCard({
  isMultiSelect,
  questions,
  isDisabled,
  isSelectAllEnabled,
  onAnswer,
  onClose,
}: QuestionSelectCardProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedByIndex, setSelectedByIndex] = useState<
    Record<number, number[]>
  >({});
  const [writeInByIndex, setWriteInByIndex] = useState<Record<number, string>>(
    {}
  );
  const [isWriteInActive, setIsWriteInActive] = useState<
    Record<number, boolean>
  >({});
  const hotkeysRef = useRef<HTMLDivElement>(null);
  const advanceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Focus moves to the card as it docks, which is what lets the number keys act right away — unless
  // the user is already typing somewhere, in which case their caret stays where it is.
  useEffect(() => {
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      (active.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName))
    )
      return;
    hotkeysRef.current?.focus({ preventScroll: true });
  }, []);

  // Clear a pending auto-advance if the card unmounts before the timer fires.
  useEffect(
    () => () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    },
    []
  );

  const total = questions.length;
  const hasPagination = total > 1;
  const currentQuestion = questions[currentIndex];
  const optionKeys = useMemo(
    () =>
      QuestionBlockHelper.uniqueKeys(
        (currentQuestion?.options ?? []).map((option) => option.title)
      ),
    [currentQuestion]
  );

  const answerFor = useCallback(
    (
      index: number,
      selected: Record<number, number[]>,
      writeIn: Record<number, string>
    ): string[] => {
      const question = questions[index];
      const picked = question ? titlesFor(question, selected[index] ?? []) : [];
      const written = (writeIn[index] ?? '').trim();
      return written ? [...picked, written] : picked;
    },
    [questions]
  );

  const buildAnswers = useCallback(
    (
      selected: Record<number, number[]>,
      writeIn: Record<number, string>
    ): CommendaAiQuestionAnswer[] =>
      questions.flatMap((_, index) => {
        const chosen = answerFor(index, selected, writeIn);
        return chosen.length > 0 ? [{ questionIndex: index, chosen }] : [];
      }),
    [questions, answerFor]
  );

  // Moves on to the next question still open, wrapping past the end, so answering the last page
  // first never strands the user on a page with nothing left to do.
  const advanceOrSubmit = useCallback(
    (selected: Record<number, number[]>, writeIn: Record<number, string>) => {
      const answers = buildAnswers(selected, writeIn);
      const allAnswered = answers.length === questions.length;
      if (allAnswered) {
        onAnswer(answers);
        return;
      }
      const answeredIndexes = new Set(answers.map((a) => a.questionIndex));
      const nextOpen = [...Array(total).keys()]
        .map((offset) => (currentIndex + 1 + offset) % total)
        .find((index) => !answeredIndexes.has(index));
      if (nextOpen === undefined || nextOpen === currentIndex) return;
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = setTimeout(
        () => setCurrentIndex(nextOpen),
        ADVANCE_DELAY_MS
      );
    },
    [buildAnswers, questions, currentIndex, total, onAnswer]
  );

  const handlePickOne = useCallback(
    (optionIndex: number) => {
      const nextSelected = {
        ...selectedByIndex,
        [currentIndex]: [optionIndex],
      };
      // Picking an option supersedes the write-in for this question.
      const nextWriteIn = { ...writeInByIndex, [currentIndex]: '' };
      setSelectedByIndex(nextSelected);
      setWriteInByIndex(nextWriteIn);
      setIsWriteInActive((prev) => ({ ...prev, [currentIndex]: false }));
      advanceOrSubmit(nextSelected, nextWriteIn);
    },
    [selectedByIndex, writeInByIndex, currentIndex, advanceOrSubmit]
  );

  // "None of these" rows stand alone: Select all skips them, and ticking one clears the rest.
  const exclusiveIndexes = useMemo(
    () =>
      isSelectAllEnabled
        ? (currentQuestion?.options ?? []).flatMap((option, index) =>
            QuestionBlockHelper.isExclusiveOption(option.title) ? [index] : []
          )
        : [],
    [isSelectAllEnabled, currentQuestion]
  );

  const handleToggleMulti = useCallback(
    (optionIndex: number) => {
      setSelectedByIndex((prev) => {
        const picked = prev[currentIndex] ?? [];
        if (picked.includes(optionIndex))
          return {
            ...prev,
            [currentIndex]: picked.filter(
              (candidate) => candidate !== optionIndex
            ),
          };
        const next = exclusiveIndexes.includes(optionIndex)
          ? [optionIndex]
          : [
              ...picked.filter(
                (candidate) => !exclusiveIndexes.includes(candidate)
              ),
              optionIndex,
            ];
        return { ...prev, [currentIndex]: next };
      });
    },
    [currentIndex, exclusiveIndexes]
  );

  const handleSelectAllOptions = useCallback(() => {
    const optionIndexes = [
      ...Array(currentQuestion?.options.length ?? 0).keys(),
    ].filter((index) => !exclusiveIndexes.includes(index));
    setSelectedByIndex((prev) => ({ ...prev, [currentIndex]: optionIndexes }));
  }, [currentQuestion, currentIndex, exclusiveIndexes]);

  const handleClearOptions = useCallback(() => {
    setSelectedByIndex((prev) => ({ ...prev, [currentIndex]: [] }));
  }, [currentIndex]);

  const handleOptionToggle = useCallback(
    (optionIndex: number) => {
      if (isMultiSelect) handleToggleMulti(optionIndex);
      else handlePickOne(optionIndex);
    },
    [isMultiSelect, handleToggleMulti, handlePickOne]
  );

  const handleNumberKey = useCallback(
    (numberKey: number) => {
      if (isDisabled) return;
      if (currentQuestion?.options[numberKey - 1])
        handleOptionToggle(numberKey - 1);
      else if (numberKey === (currentQuestion?.options.length ?? 0) + 1)
        setIsWriteInActive((prev) => ({ ...prev, [currentIndex]: true }));
    },
    [isDisabled, currentQuestion, currentIndex, handleOptionToggle]
  );
  useQuestionCardHotkeys(hotkeysRef, !isDisabled, handleNumberKey);

  const handleWriteInSubmit = useCallback(() => {
    if (isMultiSelect) return;
    advanceOrSubmit(selectedByIndex, writeInByIndex);
  }, [isMultiSelect, advanceOrSubmit, selectedByIndex, writeInByIndex]);

  const handleContinue = useCallback(() => {
    if (currentIndex < total - 1) {
      setCurrentIndex(currentIndex + 1);
      return;
    }
    onAnswer(buildAnswers(selectedByIndex, writeInByIndex));
  }, [
    currentIndex,
    total,
    onAnswer,
    buildAnswers,
    selectedByIndex,
    writeInByIndex,
  ]);

  const handleSkip = useCallback(() => {
    onAnswer(buildAnswers(selectedByIndex, writeInByIndex));
  }, [onAnswer, buildAnswers, selectedByIndex, writeInByIndex]);

  const selectedCount = (selectedByIndex[currentIndex] ?? []).length;
  const currentWriteIn = (writeInByIndex[currentIndex] ?? '').trim();
  const isCurrentAnswered = selectedCount > 0 || currentWriteIn.length > 0;
  const isWriteInActiveNow = isWriteInActive[currentIndex] ?? false;
  // Keyed on the text, not on focus: a written answer stays submittable after clicking away.
  const showContinue = isMultiSelect || currentWriteIn.length > 0;

  const optionCount = currentQuestion?.options.length ?? 0;
  const footerHint = useMemo(() => {
    if (isMultiSelect) return `${selectedCount} of ${optionCount} selected`;
    return 'Select one to continue';
  }, [isMultiSelect, selectedCount, optionCount]);

  if (!currentQuestion) return null;

  return (
    <QuestionCardShell
      label="Question"
      ariaLabel={currentQuestion.question}
      onClose={onClose}
      headerControls={
        hasPagination ? (
          <div className="flex shrink-0 items-center gap-xs">
            <IconButton
              size="sm"
              icon={<Icons.ChevronLeft className="size-4" />}
              aria-label="Previous question"
              onClick={() => setCurrentIndex((index) => Math.max(0, index - 1))}
              disabled={currentIndex === 0}
            />
            <span className="txt-extra-small-regular text-text-tertiary tabular-nums">
              {currentIndex + 1} of {total}
            </span>
            <IconButton
              size="sm"
              icon={<Icons.ChevronRight className="size-4" />}
              aria-label="Next question"
              onClick={() =>
                setCurrentIndex((index) => Math.min(total - 1, index + 1))
              }
              disabled={currentIndex === total - 1}
            />
          </div>
        ) : undefined
      }
      status={
        isMultiSelect && isSelectAllEnabled ? (
          <span className="flex items-center gap-md">
            {footerHint}
            {selectedCount < optionCount - exclusiveIndexes.length && (
              <Button
                size="sm"
                variant="secondary"
                className={QUESTION_CARD_SECONDARY_BUTTON}
                disabled={isDisabled}
                onClick={handleSelectAllOptions}
              >
                Select all
              </Button>
            )}
            {selectedCount > 0 && (
              <Button
                size="sm"
                variant="secondary"
                className={QUESTION_CARD_SECONDARY_BUTTON}
                disabled={isDisabled}
                onClick={handleClearOptions}
              >
                Clear
              </Button>
            )}
          </span>
        ) : (
          footerHint
        )
      }
      actions={
        <>
          <Button
            size="sm"
            variant="secondary"
            className={QUESTION_CARD_SECONDARY_BUTTON}
            disabled={isDisabled}
            onClick={handleSkip}
          >
            Skip
          </Button>
          {showContinue && (
            <Button
              size="sm"
              disabled={isDisabled || !isCurrentAnswered}
              onClick={handleContinue}
            >
              Continue
            </Button>
          )}
        </>
      }
    >
      <div
        ref={hotkeysRef}
        tabIndex={-1}
        className="flex flex-col gap-xl outline-none"
      >
        <div className="flex flex-col">
          <p className="txt-small-medium text-text-primary">
            {currentQuestion.question}
          </p>
          {currentQuestion.helper && (
            <p className="txt-extra-small-regular text-text-secondary">
              {currentQuestion.helper}
            </p>
          )}
        </div>
        {/* Rows and their dividers sit inside the card's padding, matching the design's Body width.
            The backend caps a question at three options, so the write-in row is always the fourth and in view; its number key focuses it.
            The max height only bites on cards saved before that cap, which can still carry long lists. */}
        <div className="flex max-h-[38vh] flex-col overflow-y-auto">
          {currentQuestion.options.map((option, optionIndex) => (
            <QuestionOptionRow
              key={optionKeys[optionIndex]}
              lead={
                isMultiSelect
                  ? { kind: 'checkbox' }
                  : { kind: 'number', value: optionIndex + 1 }
              }
              title={option.title}
              description={option.description}
              isSelected={(selectedByIndex[currentIndex] ?? []).includes(
                optionIndex
              )}
              isDisabled={isDisabled}
              onToggle={() => handleOptionToggle(optionIndex)}
            />
          ))}
          <QuestionWriteInRow
            value={writeInByIndex[currentIndex] ?? ''}
            isActive={isWriteInActiveNow}
            isDisabled={isDisabled}
            onActivate={() =>
              setIsWriteInActive((prev) => ({ ...prev, [currentIndex]: true }))
            }
            onDeactivate={() =>
              setIsWriteInActive((prev) => ({ ...prev, [currentIndex]: false }))
            }
            onChange={(value) => {
              setWriteInByIndex((prev) => ({ ...prev, [currentIndex]: value }));
              if (!isMultiSelect && value.trim())
                setSelectedByIndex((prev) => ({ ...prev, [currentIndex]: [] }));
            }}
            onSubmit={handleWriteInSubmit}
          />
        </div>
      </div>
    </QuestionCardShell>
  );
}
